// SubjectTabs/GradesTab.js - Gradebook management with real API
import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Button, Grid, Card, CardContent, Stack,
  IconButton, Chip, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Divider, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress,
  Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GradeIcon from '@mui/icons-material/Grade';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../../context/ToastContext';
import {
  fetchGradeEntries,
  createGradeEntry,
  updateGradeEntry,
  deleteGradeEntry,
  fetchStudentGradeSummary,
  fetchExams,
  fetchAssignments,
  fetchAcademicTerms
} from '../../../slices/academicsSlice';
import { fetchStudents } from '../../../slices/studentsSlice';

const GradesTab = ({ subject, selectedClass, subjectColor }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'by_student', 'by_term'
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  
  const { 
    gradeEntries, 
    gradeSummary, 
    exams, 
    assignments, 
    academicTerms,
    students,
    loading, 
    saving 
  } = useSelector((state) => ({
    gradeEntries: state.academics.gradeEntries || [],
    gradeSummary: state.academics.gradeSummary,
    exams: state.academics.exams || [],
    assignments: state.academics.assignments || [],
    academicTerms: state.academics.academicTerms || [],
    students: state.students.students || [],
    loading: state.academics.loading,
    saving: state.academics.saving
  }));

  const [formData, setFormData] = useState({
    student: '',
    grade_type: 'manual',
    title: '',
    date: new Date().toISOString().split('T')[0],
    total_marks: 100,
    obtained_marks: '',
    weight: 1.0,
    academic_term: '',
    exam: '',
    assignment: '',
    notes: ''
  });

  useEffect(() => {
    if (subject?.id && selectedClass?.id) {
      loadData();
    }
  }, [subject?.id, selectedClass?.id]);

  const loadData = () => {
    dispatch(fetchGradeEntries({ subject: subject.id, school_class: selectedClass.id }));
    dispatch(fetchExams({ subject: subject.id, school_class: selectedClass.id }));
    dispatch(fetchAssignments({ subject: subject.id, school_class: selectedClass.id }));
    dispatch(fetchAcademicTerms());
    dispatch(fetchStudents({ class_id: selectedClass.id }));
  };

  const gradeTypes = {
    exam: { label: 'اختبار', color: 'error' },
    assignment: { label: 'واجب', color: 'warning' },
    participation: { label: 'مشاركة', color: 'info' },
    project: { label: 'مشروع', color: 'secondary' },
    manual: { label: 'يدوي', color: 'default' },
    other: { label: 'أخرى', color: 'default' }
  };

  const handleOpenDialog = (grade = null) => {
    if (grade) {
      setEditingGrade(grade);
      setFormData({
        student: grade.student || grade.student_id || '',
        grade_type: grade.grade_type || 'manual',
        title: grade.title || '',
        date: grade.date || new Date().toISOString().split('T')[0],
        total_marks: grade.total_marks || 100,
        obtained_marks: grade.obtained_marks || '',
        weight: grade.weight || 1.0,
        academic_term: grade.academic_term || grade.academic_term_id || '',
        exam: grade.exam || grade.exam_id || '',
        assignment: grade.assignment || grade.assignment_id || '',
        notes: grade.notes || ''
      });
    } else {
      setEditingGrade(null);
      setFormData({
        student: '',
        grade_type: 'manual',
        title: '',
        date: new Date().toISOString().split('T')[0],
        total_marks: 100,
        obtained_marks: '',
        weight: 1.0,
        academic_term: '',
        exam: '',
        assignment: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGrade(null);
  };

  const handleSaveGrade = async () => {
    try {
      const gradeData = {
        student: formData.student,
        subject: subject.id,
        school_class: selectedClass.id,
        grade_type: formData.grade_type,
        title: formData.title,
        date: formData.date,
        total_marks: parseFloat(formData.total_marks),
        obtained_marks: parseFloat(formData.obtained_marks),
        weight: parseFloat(formData.weight),
        academic_term: formData.academic_term || null,
        exam: formData.exam || null,
        assignment: formData.assignment || null,
        notes: formData.notes || ''
      };

      if (editingGrade) {
        await dispatch(updateGradeEntry({ id: editingGrade.id, ...gradeData })).unwrap();
        showToast('✅ تم تحديث الدرجة بنجاح');
      } else {
        await dispatch(createGradeEntry(gradeData)).unwrap();
        showToast('✅ تم إضافة الدرجة بنجاح');
      }
      
      handleCloseDialog();
      loadData();
    } catch (error) {
      showToast('❌ فشل في حفظ الدرجة');
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الدرجة؟')) {
      return;
    }
    
    try {
      await dispatch(deleteGradeEntry(gradeId)).unwrap();
      showToast('✅ تم حذف الدرجة بنجاح');
      loadData();
    } catch (error) {
      showToast('❌ فشل في حذف الدرجة');
    }
  };

  // Filter grade entries for this subject
  let filteredGrades = gradeEntries.filter(
    g => g.subject === subject.id || g.subject_id === subject.id
  );

  // Apply additional filters
  if (viewMode === 'by_student' && selectedStudent) {
    filteredGrades = filteredGrades.filter(
      g => g.student === selectedStudent || g.student_id === selectedStudent
    );
  }

  if (selectedTerm) {
    filteredGrades = filteredGrades.filter(
      g => g.academic_term === selectedTerm || g.academic_term_id === selectedTerm
    );
  }

  // Calculate statistics
  const totalGrades = filteredGrades.length;
  const averageGrade = filteredGrades.length > 0
    ? filteredGrades.reduce((sum, g) => sum + parseFloat(g.percentage || 0), 0) / filteredGrades.length
    : 0;

  // Group by student for summary view
  const gradesByStudent = {};
  filteredGrades.forEach(grade => {
    const studentId = grade.student || grade.student_id;
    if (!gradesByStudent[studentId]) {
      gradesByStudent[studentId] = {
        student_id: studentId,
        student_name: grade.student_name || 'غير معروف',
        grades: [],
        totalWeight: 0,
        weightedSum: 0
      };
    }
    gradesByStudent[studentId].grades.push(grade);
    if (grade.percentage) {
      const weight = parseFloat(grade.weight || 1);
      gradesByStudent[studentId].weightedSum += parseFloat(grade.percentage) * weight;
      gradesByStudent[studentId].totalWeight += weight;
    }
  });

  // Calculate averages for each student
  Object.keys(gradesByStudent).forEach(studentId => {
    const studentData = gradesByStudent[studentId];
    studentData.average = studentData.totalWeight > 0
      ? studentData.weightedSum / studentData.totalWeight
      : null;
  });

  if (loading && filteredGrades.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack useFlexGap direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: subjectColor }}>
              <GradeIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                إدارة الدرجات
              </Typography>
              <Typography variant="body2" color="textSecondary">
                نظام الدرجات لمادة {subject.name}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              bgcolor: subjectColor,
              '&:hover': { bgcolor: `${subjectColor}dd` },
              borderRadius: 2
            }}
          >
            إضافة درجة
          </Button>
        </Stack>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color={subjectColor}>
                {totalGrades}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                إجمالي الدرجات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {Math.round(averageGrade)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                متوسط الدرجات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {Object.keys(gradesByStudent).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                عدد الطلاب
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {filteredGrades.filter(g => g.grade_type === 'exam').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                درجات اختبارات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack useFlexGap direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Tabs
            value={viewMode}
            onChange={(e, newValue) => {
              setViewMode(newValue);
              if (newValue !== 'by_student') setSelectedStudent(null);
            }}
            sx={{ minWidth: 200 }}
          >
            <Tab label="الكل" value="all" />
            <Tab label="حسب الطالب" value="by_student" />
          </Tabs>

          {viewMode === 'by_student' && (
            <Autocomplete
              options={students}
              getOptionLabel={(option) => `${option.first_name} ${option.second_name || ''}`.trim()}
              value={students.find(s => s.id === selectedStudent) || null}
              onChange={(event, newValue) => {
                setSelectedStudent(newValue?.id || null);
              }}
              sx={{ minWidth: 200 }}
              renderInput={(params) => (
                <TextField {...params} label="اختر الطالب" size="small" />
              )}
            />
          )}

          <Autocomplete
            options={academicTerms}
            getOptionLabel={(option) => option.name || ''}
            value={academicTerms.find(t => t.id === selectedTerm) || null}
            onChange={(event, newValue) => {
              setSelectedTerm(newValue?.id || null);
            }}
            sx={{ minWidth: 200 }}
            renderInput={(params) => (
              <TextField {...params} label="الفصل الدراسي" size="small" />
            )}
          />
        </Stack>
      </Paper>

      {/* Grades Table */}
      {filteredGrades.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            لا توجد درجات مسجلة
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ابدأ بإضافة درجة جديدة
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>الطالب</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>النوع</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>العنوان</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>التاريخ</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>الدرجة</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>النسبة</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>الوزن</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGrades.map((grade) => (
                <TableRow key={grade.id} hover>
                  <TableCell>{grade.student_name || 'غير معروف'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={gradeTypes[grade.grade_type]?.label || grade.grade_type}
                      color={gradeTypes[grade.grade_type]?.color || 'default'}
                    />
                  </TableCell>
                  <TableCell>{grade.title}</TableCell>
                  <TableCell>
                    {new Date(grade.date).toLocaleDateString('en-GB')}
                  </TableCell>
                  <TableCell align="right">
                    {grade.obtained_marks} / {grade.total_marks}
                  </TableCell>
                  <TableCell align="right">
                    {grade.percentage ? (
                      <Chip
                        size="small"
                        label={`${parseFloat(grade.percentage).toFixed(1)}%`}
                        color={
                          grade.percentage >= 70 ? 'success' :
                          grade.percentage >= 50 ? 'warning' : 'error'
                        }
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">{grade.weight}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(grade)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteGrade(grade.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Student Summary View */}
      {viewMode === 'by_student' && Object.keys(gradesByStudent).length > 0 && (
        <Paper elevation={2} sx={{ mt: 3, p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            ملخص الدرجات حسب الطالب
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.values(gradesByStudent).map((studentData) => (
              <Grid item xs={12} md={6} key={studentData.student_id}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" fontWeight="bold">
                        {studentData.student_name}
                      </Typography>
                      {studentData.average !== null ? (
                        <Chip
                          label={`${Math.round(studentData.average)}%`}
                          color={
                            studentData.average >= 70 ? 'success' :
                            studentData.average >= 50 ? 'warning' : 'error'
                          }
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          لا توجد درجات
                        </Typography>
                      )}
                    </Stack>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {studentData.grades.length} درجة مسجلة
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Add/Edit Grade Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, direction: 'rtl' }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${subjectColor} 0%, ${subjectColor}dd 100%)`,
          color: 'white',
          m: -3,
          mb: 2,
          borderRadius: '12px 12px 0 0'
        }}>
          <Stack useFlexGap direction="row" spacing={2} alignItems="center" justifyContent="center">
            <GradeIcon />
            <Typography variant="h6">
              {editingGrade ? 'تعديل الدرجة' : 'إضافة درجة جديدة'}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={students}
                getOptionLabel={(option) => `${option.first_name} ${option.second_name || ''}`.trim()}
                value={students.find(s => s.id === formData.student) || null}
                onChange={(event, newValue) => {
                  setFormData({...formData, student: newValue?.id || ''});
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="الطالب"
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>نوع الدرجة</InputLabel>
                <Select
                  value={formData.grade_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({
                      ...formData,
                      grade_type: newType,
                      exam: newType !== 'exam' ? '' : formData.exam,
                      assignment: newType !== 'assignment' ? '' : formData.assignment
                    });
                  }}
                  label="نوع الدرجة"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="exam">اختبار</MenuItem>
                  <MenuItem value="assignment">واجب</MenuItem>
                  <MenuItem value="participation">مشاركة</MenuItem>
                  <MenuItem value="project">مشروع</MenuItem>
                  <MenuItem value="manual">يدوي</MenuItem>
                  <MenuItem value="other">أخرى</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.grade_type === 'exam' && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={exams.filter(e => e.subject === subject.id || e.subject_id === subject.id)}
                  getOptionLabel={(option) => option.exam_name || ''}
                  value={exams.find(e => e.id === formData.exam) || null}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      exam: newValue?.id || '',
                      title: newValue?.exam_name || formData.title,
                      total_marks: newValue?.total_marks || formData.total_marks
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الاختبار المرتبط (اختياري)"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
            )}

            {formData.grade_type === 'assignment' && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={assignments.filter(a => a.subject === subject.id || a.subject_id === subject.id)}
                  getOptionLabel={(option) => option.title || ''}
                  value={assignments.find(a => a.id === formData.assignment) || null}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      assignment: newValue?.id || '',
                      title: newValue?.title || formData.title,
                      total_marks: newValue?.total_marks || formData.total_marks
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الواجب المرتبط (اختياري)"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} md={formData.grade_type === 'exam' || formData.grade_type === 'assignment' ? 6 : 12}>
              <TextField
                label="عنوان الدرجة"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                fullWidth
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="التاريخ"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={academicTerms}
                getOptionLabel={(option) => option.name || ''}
                value={academicTerms.find(t => t.id === formData.academic_term) || null}
                onChange={(event, newValue) => {
                  setFormData({...formData, academic_term: newValue?.id || ''});
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="الفصل الدراسي (اختياري)"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="إجمالي الدرجات"
                type="number"
                value={formData.total_marks}
                onChange={(e) => setFormData({...formData, total_marks: e.target.value})}
                fullWidth
                required
                inputProps={{ min: 1, step: 0.01 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="الدرجة المحصلة"
                type="number"
                value={formData.obtained_marks}
                onChange={(e) => setFormData({...formData, obtained_marks: e.target.value})}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01, max: formData.total_marks }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="الوزن في المعدل"
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: e.target.value})}
                fullWidth
                inputProps={{ min: 0.1, step: 0.1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {formData.obtained_marks && formData.total_marks && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  النسبة المئوية: {((parseFloat(formData.obtained_marks) / parseFloat(formData.total_marks)) * 100).toFixed(1)}%
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="ملاحظات (اختياري)"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                fullWidth
                multiline
                rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ minWidth: 100, borderRadius: 2 }}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleSaveGrade}
            variant="contained"
            disabled={saving || !formData.student || !formData.title || !formData.obtained_marks}
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              bgcolor: subjectColor,
              '&:hover': { bgcolor: `${subjectColor}dd` }
            }}
          >
            {saving ? 'جاري الحفظ...' : editingGrade ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GradesTab;
